import Splide from '@splidejs/splide';
import { Build, Component, Element, Host, Prop, Watch, h } from '@stencil/core';
import { isEnterKey, renderArticleImg } from '../../helpers/helpers';
import type {
  Product,
  SplideComponentConfig,
} from '../../helpers/utils/interfaces';

@Component({
  tag: 'cms-material-tools-slider',
  styleUrl: 'cms-material-tools-slider.scss',
  shadow: true,
})
export class CmsMaterialToolsSlider {
  private articles: Product[];
  private splideNode: HTMLElement;
  private splideObj: Splide;
  private displayCountPagesNode: HTMLElement;
  private currentPageNode: HTMLElement;
  private currentTargetUrl: string;
  private categoryCtaNode: HTMLCmsButtonElement;
  private splideTranslations: SplideComponentConfig;
  private readonly splideConfig = {
    type: 'slide',
    perMove: 1,
    perPage: 1,
    slideFocus: false,
    pagination: false,
    trimSpace: true,
    autoHeight: true,
    heightRatio: 0.8,
    focusableNodes: 'li',
    role: 'carousel',
  };

  /**
   * Displayed the Badge Name of the Slider like Material
   */
  @Prop() badge: string;

  /**
   * get the Products data as JSON
   */
  @Prop() products!: Product[] | string;

  /**
   * Displayed the Button Label like Zum Produkt
   */
  @Prop() buttonLabel: string;

  /**
   * Displayed the Pagination separator like von
   */
  @Prop() paginationSeparator: string;

  /**
   * Set the tracking namespace (optional)
   */
  @Prop({ mutable: true }) namespace?: string;

  /**
   * set the accessible name
   */
  @Prop() accessibleName: string;

  /**
   * Set the translations for splide
   */
  @Prop() translations: string;

  @Element() hostElement: HTMLElement;

  @Watch('products')
  private productsWatcher(newValue: Product[] | string) {
    if (newValue) {
      this.articles =
        typeof newValue === 'string' ? JSON.parse(newValue) : newValue;
    }
  }

  @Watch('translations')
  private translationsWatcher(newValue: SplideComponentConfig | string) {
    if (newValue) {
      this.splideTranslations =
        typeof newValue === 'string'
          ? (JSON.parse(newValue) as SplideComponentConfig)
          : newValue;
    }
  }

  private mapArticles() {
    if (this.articles !== undefined) {
      return this.articles.map((article: Product, index: number) => {
        const { detailsUrl, name, image } = article;
        if (index === 0) {
          this.currentTargetUrl = detailsUrl;
        }
        return (
          <li
            onClick={() => this.openUrl(this.currentTargetUrl)}
            key={detailsUrl}
            onKeyDown={(event) => {
              if (isEnterKey(event)) {
                this.openUrl(this.currentTargetUrl);
              }
            }}
            data-targeturl={detailsUrl}
            class={'splide__slide article-slider-list'}
            {...{
              ariaHidden: false,
              tabIndex: 0,
            }}
          >
            <div class={'splide__slide__container'}>
              {name !== undefined ? this.renderArticleName(name) : null}
              {name && image !== undefined
                ? this.renderArticleImageWrapper(name, image)
                : null}
            </div>
          </li>
        );
      });
    }
  }

  private renderArticleImageWrapper(name: string, image: string) {
    return (
      <div class={'article-image-wrapper'}>{renderArticleImg(name, image)}</div>
    );
  }

  private renderArticleName(name: string) {
    const headlineName =
      name.length <= 40 ? name : this.shorterArticleName(name);
    return (
      <div class={'article-name-wrapper'} innerHTML={headlineName} />
    ) as HTMLElement;
  }

  private shorterArticleName(name: string) {
    return `${name.substring(0, 40)} ...`;
  }

  private mountSplide() {
    if (
      !Build.isBrowser ||
      !(this.products !== undefined && this.splideNode !== undefined)
    ) {
      return;
    }
    this.splideObj = new Splide(this.splideNode, this.splideConfig);
    this.splideEventController('mounted', this.onMounted.bind(this));
    this.splideObj.mount();
    this.splideEventController('move', this.onMove.bind(this));
  }

  private splideEventController(splideEvent: string, cb): void {
    if (this.splideObj !== undefined) {
      this.splideObj.on(splideEvent, (returnedValue) => {
        cb(returnedValue);
      });
    }
  }

  private onMounted() {
    if (this.splideObj !== undefined) {
      this.displayCountPagesNode.innerHTML = this.splideObj.length.toString();
      this.currentPageNode.innerHTML = (this.splideObj.index + 1).toString();
    }
  }

  private getTargetUrl(splideObj: Splide) {
    if (splideObj !== undefined) {
      return splideObj.Components.Slides.getAt(this.splideObj.index).slide
        .dataset.targeturl;
    }
  }

  private onMove(returnedValue: number) {
    if (this.splideObj !== undefined) {
      this.currentTargetUrl = this.getTargetUrl(this.splideObj);
      this.categoryCtaNode.href = this.currentTargetUrl;
      this.currentPageNode.innerHTML = (returnedValue + 1).toString();
    }
  }

  private openUrl(url: string) {
    if (url !== undefined) {
      const win = window.open(url, '_self');
      if (win != null) {
        win.focus();
      }
    }
  }

  connectedCallback() {
    if (this.products !== undefined) {
      this.productsWatcher(this.products);
    }
  }

  componentWillLoad() {
    if (this.translations !== undefined) {
      this.translationsWatcher(this.translations);
    }
  }

  componentDidLoad() {
    this.categoryCtaNode =
      this.hostElement.shadowRoot.querySelector('.category-cta');
    this.splideNode = this.hostElement.shadowRoot.querySelector('.splide');
    this.displayCountPagesNode =
      this.hostElement.shadowRoot.querySelector('.count-pages');
    this.currentPageNode =
      this.hostElement.shadowRoot.querySelector('.current-page');
    if (this.products !== undefined) {
      this.mountSplide();
    }
  }

  render() {
    return (
      <Host
        class={'cms-material-tools-slider'}
        aria-label={this.accessibleName}
      >
        <div class={'cms-material-tools-slider-inner'}>
          {this.badge !== undefined ? (
            <div class={'badge-wrapper'}>
              <cms-badge name={this.badge} theme='primary' size='badge-md' />
            </div>
          ) : null}
          <div class={'splide'}>
            <div class={'splide-inner'}>
              <div class={'splide__slider'}>
                <div class={'splide__track'}>
                  <ul class={'splide__list'}>{this.mapArticles()}</ul>
                </div>

                <div class='splide__arrows'>
                  <cms-button
                    namespace={`${this.namespace}-arrow-left`}
                    class={'splide-arrow splide__arrow--prev'}
                    theme='secondary'
                    size='button-sm'
                    type='button'
                    icon='arrowLeft'
                    iconSize='md'
                    tagTitle={this.splideTranslations?.arrowLeftLabel}
                    focusable={false}
                  />
                  <cms-button
                    namespace={`${this.namespace}-arrow-right`}
                    class={'splide-arrow splide__arrow--next'}
                    theme='secondary'
                    size='button-sm'
                    type='button'
                    icon='arrowRight'
                    iconSize='md'
                    tagTitle={this.splideTranslations?.arrowRightLabel}
                    focusable={false}
                  />
                </div>
                <div class='pagination-wrapper'>
                  <div class='pagination'>
                    <span class='current-page' />
                    <span class='of-pages'>{this.paginationSeparator}</span>
                    <span class='count-pages' />
                  </div>

                  <cms-button
                    class={'category-cta'}
                    theme='secondary'
                    size='button-md'
                    href={this.currentTargetUrl}
                    rel='noopener'
                    target='_self'
                    label={this.buttonLabel}
                    tagTitle={this.buttonLabel}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
